import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CacheService } from '@core/cache/cache.service';
import { CategoryService } from '@core/category/category.service';
import { Category } from '@core/category/category.types';
import { MerchantService } from '@core/merchant/merchant.service';
import { Merchant } from '@core/merchant/merchant.types';
import { Picture } from '@core/picture/picture.types';
import { Attribute, CustomDescription, Product, ProductCount, VariantItem } from '@core/product//product.types';
import { EnvironmentService } from '@core/utils/environment/environment.service';
import { DEFAULT_HTTP_OPTIONS_NO_EXPIRE, HttpOptions } from '@core/utils/http-options.types';
import { environment } from '@env/environment';
import { LangDefinition, TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';

/**
 * The default product count.
 */
const DEFAULT_PRODUCT_COUNT: ProductCount = {
  nbProducts: 0,
  nbVariantItems: 0,
  nbInventoryItems: 0
};

/**
 * Service responsible for managing products.
 */
@Injectable({
  providedIn: 'root'
})
export class ProductService {

  /**
   * The merchant associated with the products.
   */
  private _merchant: Merchant;

  /**
   * The list of all the categories of the merchant.
   */
  private _categories: Category[];

  /**
   * The selected product.
   */
  private _product: BehaviorSubject<Product | null> = new BehaviorSubject(null);

  /**
   * The number of products.
   */
  private _productCount: BehaviorSubject<ProductCount> = new BehaviorSubject({
    nbProducts: 0,
    nbVariantItems: 0,
    nbInventoryItems: 0
  });

  /**
   * The list of products.
   */
  private _products: BehaviorSubject<Product[]> = new BehaviorSubject([]);

  /**
   * The available languages.
   */
  private _availableLanguages: LangDefinition[];

  /**
   * The cache namespace.
   */
  private _cacheNamespace: string = 'product';

  /**
   * Creates an instance of ProductService.
   *
   * @param _environmentService The environment service.
   * @param _httpClient The HTTP client.
   * @param _translocoService The Transloco service.
   * @param _cacheService The cache service.
   * @param _merchantService The merchant service.
   * @param _categoryService The category service.
   */
  constructor(
    private _environmentService: EnvironmentService,
    private _httpClient: HttpClient,
    private _translocoService: TranslocoService,
    private _cacheService: CacheService,
    private _merchantService: MerchantService,
    private _categoryService: CategoryService
  ) {
      // Get the merchant
      this._merchantService.merchant$
        .subscribe((merchant: Merchant) => {
          const reload: boolean = this._merchant?._id.toString() !== merchant?._id.toString();
          this._merchant = merchant;
          if (reload) {
            this._product.next(null);
            this._productCount.next({
              nbProducts: 0,
              nbVariantItems: 0,
              nbInventoryItems: 0
            });
            this._products.next([]);
            this.getProductTags().subscribe();
          }
        });

      // Get all the categories.
      this._categoryService.categories$
        .subscribe((categories: Category[]) => this._categories = categories);
      
      this._availableLanguages = this._translocoService.getAvailableLangs() as LangDefinition[];

      // Subscribe to language changes
      this._translocoService.langChanges$.subscribe((activeLang) => {
        let products: Product[] = this._products.getValue();
        products = products.map(product => this.restoreProduct(product))
        this._products.next(products);
        let product: Product = this._product.getValue();
        product = this.restoreProduct(product);
        this._product.next(product);
        this._cacheService.delete(this._cacheNamespace);
      });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Returns an observable of the products array.
   * @returns An observable of the products array.
   */
  get products$(): Observable<Product[]> {
    return this._products.asObservable();
  }

  /**
   * Returns an observable of the current product.
   * @returns An observable of the current product.
   */
  get product$(): Observable<Product>
   {
       return this._product.asObservable();
   }

  /**
   * Returns an observable of the current product count.
   * @returns An observable of type ProductCount.
   */
   get productCount$(): Observable<ProductCount>
    {
        return this._productCount.asObservable();
    }

   
  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Restores the product data from the dictionary.
   * @param product - The product to restore.
   * @returns The restored product.
   */
  private _restoreFromDictionary(product: Product): Product {
    if (!product.dictionary) {
      return;
    }
    for (const lang of this._availableLanguages) {
      if (!product.dictionary[lang.id]) {
        return;
      }
    };
    const currentLanguage: string = this._translocoService.getActiveLang();
    const defaultLanguage: string = this._merchant?.website?.defaultLanguage ?? 'fr';
    const currentProductLanguage: string = product.currentLanguage ?? defaultLanguage;
    const hasKey = (key: string, lang: string) => product.dictionary[lang][key] != null;
    const hasNonEmptyKey = (key: string, lang: string) => hasKey(key, lang) && product.dictionary[lang][key].text !== '';
    const getKey = (key: string) => {
      if (hasNonEmptyKey(key, currentLanguage)) {
        return product.dictionary[currentLanguage][key].text
      } else if (hasNonEmptyKey(key, defaultLanguage)) {
        return product.dictionary[defaultLanguage][key].text;
      } else {
        for (const lang of this._availableLanguages) {
          if (hasNonEmptyKey(key, lang.id)) {
            return product.dictionary[lang.id][key].text;
          }
        }
        return '';
      };
    };
    const keyExists = (key: string) => getKey(key) && getKey(key) !== '';
    // Retrieve from dictionary
    product.name = getKey('name');
    product.description = getKey('description');
    // Retrieve the custom descriptions
    const customDescriptions: CustomDescription[] = [];
    let idx = 0;
    while (true) {
      const name = `customDescription_${idx}_name`;
      const description = `customDescription_${idx}_description`;
      if (!keyExists(name) || !keyExists(description)) {
        break;
      }
      customDescriptions.push({
        name: getKey(name),
        description: getKey(description),
        icon: product.customDescriptions[idx]?.icon || null
      });
      idx++;
    }
    product.customDescriptions = customDescriptions;
    // Retrieve the tags
    product.tags = [];
    idx = 0;
    while (true) {
      const key = `tag_${idx}`;
      if (!keyExists(key)) {
        break;
      }
      product.tags.push(getKey(key));
      idx++;
    }
    // Retrieve the custom fields
    idx = 0;
    while (true) {
      const label = `customField_${idx}_label`;
      const description = `customField_${idx}_description`;
      if (!keyExists(label) || !keyExists(description)) {
        break;
      }
      product.customFields[idx].label = getKey(label);
      product.customFields[idx].description = getKey(description);
      idx++;
    }
    // Retrieve the attributes
    const attributes: Attribute[] = [];
    idx = 0;
    while (true) {
      const key = `attribute_${idx}_name`;
      if (!keyExists(key)) {
        break;
      }
      const attribute: Attribute = {
        type: product.attributes[idx].type,
        name: getKey(key),
        description: getKey(`attribute_${idx}_description`),
        options: [],
        displayedAsFilter: product.attributes[idx].displayedAsFilter
      };
      let idx2 = 0;
      while (true) {
        const key2 = `attribute_${idx}_option_${idx2}`;
        if (!keyExists(key2)) {
          break;
        }
        attribute.options.push(getKey(key2));
        idx2++;
      }
      attributes.push(attribute);
      idx++;
    }
    product.attributes = attributes;
    // Retrieve the variant items
    for (const variantItem of product.variantItems) {
      for (const attribute of variantItem.attributes) {
        for (const key of Object.keys(product.dictionary[currentProductLanguage])) {
          if (key.match(/attribute_\d+_name/g) && product.dictionary[currentProductLanguage][key].text === attribute.name) {
            attribute.name = getKey(key);
            break;
          }
        }
        for (const key of Object.keys(product.dictionary[currentProductLanguage])) {
          if (key.match(/attribute_\d+_option_\d+/g) && product.dictionary[currentProductLanguage][key].text === attribute.option) {
            attribute.option = getKey(key);
            break;
          }
        }
      }
    }
    // Retrieve the discount messages of the variant items
    product.variantItems.forEach((variantItem: VariantItem, idx: number) => {
      variantItem.discountMessage = getKey(`variantItem_${idx}_discountMessage`);
    });
    product.currentLanguage = currentLanguage;
    return product;
  }

  /**
   * Returns the SrcSet and sizes of the photos of the merchant.
   * @param {string} merchantName The name of the merchant.
   * @param {string[]} filenames The filenames of the photos.
   * @returns {{srcset: string, sizes: string}} The SrcSet and sizes of the photos.
   */
  private _getSrcSet(merchantName: string, filenames: string[]): {srcset: string, sizes: string} {
    const srcset: string[] = [];
    const breakpoints: any = {
      'xsmall': 100,
      'small': 320,
      'medium': 768,
      'large': 1024,
      'xlarge': 1440
    };
    for (const filename of filenames) {
      try {
        const width: number = parseInt(filename.split('-')[1]);
        const breakpoint: string = Object.keys(breakpoints).find((key) => breakpoints[key] === width);
        if (!breakpoint) {
          continue;
        }
        srcset.push(`${environment.qart.imagesUrl}/${merchantName}/${filename} ${width}w`);
      } catch (e) {
        continue;
      }
    }
    return {
      srcset: srcset.join(', ') || null,
      sizes: '(min-width: 1280px) 1440px, (min-width: 960px) 1024px, (min-width: 600px) 768px, 100vw'
    };
  };

  /**
   * Fill in missing data for a product with default values.
   * @param product - The product to fill in missing data for.
   */
  private _fillInMissingData(product: Product): Product {
    if (!product.aiGenerated) {
      product.aiGenerated = {
        title: false,
        description: false
      };
    }
    if (!product.customFields) {
      product.customFields = [];
    }
    return product;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Restores a Product object from a dictionary.
   * @param data The dictionary containing the Product data.
   * @returns The restored Product object.
   */
  restoreProduct(data: any): Product {
    let product: Product = new Product().deserialize(data);
    product = this._fillInMissingData(product);
    product = this._restoreFromDictionary(product);
    return product;
  }

  /**
   * Retrieves a list of products based on the provided filters.
   * @param filters An object containing the filters to apply to the search.
   * @param options - The options related to cache and propagation.
   * @returns The requested product objects.
   */
  getProducts(filters: any = {}, options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Observable<Product[]> {
    // If there is no merchant, return an empty array
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return of([]);
    }
    filters['displayOnline'] = true;
    filters['valid'] = true;
    // If a specific category is not specified, then make sure to only retrieve products from categories that are displayed online
    if (!('categoryIds' in filters) && this._categories && this._categories.length > 0) {
      filters['categoryIds'] = this._categories
        .filter(category => category.displayOnline)
        .map(category => category._id);
    }
    // If the products are already loaded, return them
    const cacheParams: any = { merchantId: this._merchant._id, filters };
    if (options.cache && this._cacheService.has(this._cacheNamespace, 'products', cacheParams)) {
      const products: Product[] = this._cacheService.get(this._cacheNamespace, 'products', cacheParams)
        .map(product => this.restoreProduct(product));
      if (options.forcePropagate) {
        this._products.next(products);
      }
      return of(products);
    }
    // Otherwise, load the products from the backend
    return this._httpClient.post<Product[]>(
      `${this._environmentService.getApiUrl()}/${environment.qart.apiVersion}/merchants/${this._merchant._id}/products/search`,
      filters,
      {
        headers: new HttpHeaders().set('Content-Type', 'application/json'),
        withCredentials: true
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(`Error: ${error}`);
          return of([]);
        }),
        map((products: Product[]) => products.map(product => this.restoreProduct(product))),
        // Handle the logic of the cache and the propagation
        tap((products: Product[]) => {
          if (options.concatenateResults && this._products.value != null) {
            products = this._products.value.concat(products);
          }
          if (options.cache) {
            this._cacheService.set(this._cacheNamespace, 'products', cacheParams, products, options.expire);
          }
          if (options.propagate) {
            this._products.next(products);
          }
        })
      );
  }
  
  /**
   * Counts the number of products based on the given filters.
   * @param filters - The filters to apply to the count.
   * @param options - The options related to cache and propagation.
   * @returns The product counts.
   */
  countProducts(filters: any = {}, options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Observable<ProductCount> {
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return of(DEFAULT_PRODUCT_COUNT);
    }
    filters['displayOnline'] = true;
    filters['valid'] = true;
    // If a specific category is not specified, then make sure to only count products from categories that are displayed online
    if (!('categoryIds' in filters) && this._categories && this._categories.length > 0) {
      filters['categoryIds'] = this._categories
        .filter(category => category.displayOnline)
        .map(category => category._id);
    }
    // If the product count is already loaded, return them
    const cacheParams: any = { merchantId: this._merchant._id, filters };
    if (options.cache && this._cacheService.has(this._cacheNamespace, 'productCount', cacheParams)) {
      const productCount: ProductCount = this._cacheService.get(this._cacheNamespace, 'productCount', cacheParams);
      if (options.forcePropagate) {
        this._productCount.next(productCount);
      }
      return of(productCount);
    }
    // Otherwise, load the product count from the backend
    return this._httpClient.post<ProductCount>(
      `${this._environmentService.getApiUrl()}/${environment.qart.apiVersion}/merchants/${this._merchant._id}/products/count`,
      filters,
      {
        headers: new HttpHeaders().set('Content-Type', 'application/json'),
        withCredentials: true
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(`Error: ${error}`);
          return of(DEFAULT_PRODUCT_COUNT);
        }),
        // Handle the logic of the cache and the propagation
        tap((productCount: ProductCount) => {
          if (options.cache) {
            this._cacheService.set(this._cacheNamespace, 'productCount', cacheParams, productCount, options.expire);
          }
          if (options.propagate) {
            this._productCount.next(productCount);
          }
        })
      );
  }

  
  /**
   * Returns the count of all products for each category of the current merchant.
   * @param options - The options related to cache and propagation.
   * @returns An object containing the count of all products for each category of the current merchant.
   */
  getAllProductCounts(options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Observable<any> {
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return of(null);
    }
    // If the product count is already loaded, return them
    const cacheParams: any = { merchantId: this._merchant._id };
    if (options.cache && this._cacheService.has(this._cacheNamespace, 'allProductCounts', cacheParams)) {
      const productCount: ProductCount = this._cacheService.get(this._cacheNamespace, 'allProductCounts', cacheParams);
      return of(productCount);
    }
    // Otherwise, load the product count from the backend
    return this._httpClient.get<any>(
      `${this._environmentService.getApiUrl()}/${environment.qart.apiVersion}/merchants/${this._merchant._id}/categories/count`,
      {
        headers: new HttpHeaders().set('Content-Type', 'application/json'),
        withCredentials: true
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.log('Error:', error);
          return of(null);
        }),
        map((productCounts: any) => {
          const response: any = {};
          for (const productCount of productCounts) {
            response[productCount.categoryId] = productCount.count;
          }
          return response;
        }),
        // Handle the logic of the cache and the propagation
        tap((allProductCounts: any) => {
          if (options.cache) {
            this._cacheService.set(this._cacheNamespace, 'allProductCounts', cacheParams, allProductCounts, options.expire);
          }
        })
      );
  }

  /**
   * Retrieves a product by its ID.
   * @param productId - The ID of the product to retrieve.
   * @param options - The options related to cache and propagation.
   * @returns The retrieved product object.
   */
  getProductById(productId: string, options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Observable<Product> {
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return of(null);
    }
    // If the product is already loaded, return it.
    // If the product is not found, do not record the cache miss yet.
    const cacheParams: any = { merchantId: this._merchant._id, productId };
    if (options.cache) {
      if (this._cacheService.has(this._cacheNamespace, 'product', cacheParams, false)) {
        this._cacheService.updateStats(true);
        let product: Product = this._cacheService.get(this._cacheNamespace, 'product', cacheParams);
        product = this.restoreProduct(product);
        if (options.forcePropagate) {
          this._product.next(product);
        }
        return of(product);
      }
      // Check if the product is one of the previously loaded sets of products
      const productSets: Product[][] = this._cacheService.get(this._cacheNamespace, 'products', null);
      if (productSets) {
        for (const products of productSets) {
          let product: Product = products.find(p => p._id === productId);
          if (product) {
            product = this.restoreProduct(product);
            if (options.cache) {
              this._cacheService.set(this._cacheNamespace, 'product', cacheParams, product, options.expire);
            }
            if (options.propagate) {
              this._product.next(product);
            }
            // Record the cache hit.
            this._cacheService.updateStats(true);
            return of(product);
          }
        }
      }
      // Record the cache miss.
      this._cacheService.updateStats(false);
    }
    // Otherwise, load the product from the backend
    const filters: any = {
      productId,
      limit: 1
    };
    return this._httpClient.post<Product[]>(
      `${this._environmentService.getApiUrl()}/${environment.qart.apiVersion}/merchants/${this._merchant._id}/products/search`,
      filters,
      {
        headers: new HttpHeaders().set('Content-Type', 'application/json'),
        withCredentials: true
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(`Error: ${error}`);
          return of([]);
        }),
        map((products: Product[]) => products.length > 0 ? this.restoreProduct(products[0]) : null),
        // Check if the product belongs to a hidden category
        map((product: Product) => {
          if (product && this._categories && this._categories.length > 0) {
            const category: Category = this._categories.find(category => category._id === product.categoryId);
            if (category && !category.displayOnline) {
              return null;
            }
          }
          return product;
        }),
        // Handle the logic of the cache and the propagation
        tap((product: Product) => {
          if (options.cache) {
            this._cacheService.set(this._cacheNamespace, 'product', cacheParams, product, options.expire);
          }
          if (options.propagate) {
            this._product.next(product);
          }
        })
      );
  }


  /**
   * Retrieves a product by its SEO URI.
   * @param seoUri The SEO URI of the product to retrieve.
   * @param options - The options related to cache and propagation.
   * @returns The requested product object.
   */
  getProductBySEOUri(seoUri: string, options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Observable<Product | null> {
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return of(null);
    }
    // If the product is already loaded, return it.
    // If the product is not found, do not record the cache miss yet.
    const cacheParams: any = { merchantId: this._merchant._id, seoUri };
    if (options.cache) {
      if (this._cacheService.has(this._cacheNamespace, 'product', cacheParams, false)) {
        this._cacheService.updateStats(true);
        let product: Product = this._cacheService.get(this._cacheNamespace, 'product', cacheParams);
        product = this.restoreProduct(product);
        if (options.forcePropagate) {
          this._product.next(product);
        }
        return of(product);
      }
      // Check if the product is one of the previously loaded sets of products
      const productSets: Product[][] = this._cacheService.get(this._cacheNamespace, 'products', null);
      if (productSets) {
        for (const products of productSets) {
          let product: Product = products.find(p => p.SEO.uri === seoUri);
          if (product) {
            product = this.restoreProduct(product);
            if (options.cache) {
              this._cacheService.set(this._cacheNamespace, 'product', cacheParams, product, options.expire);
            }
            if (options.propagate) {
              this._product.next(product);
            }
            // Record the cache hit.
            this._cacheService.updateStats(true);
            return of(product);
          }
        }
      }
      // Record the cache miss.
      this._cacheService.updateStats(false);
    }
    const filters: any = {
      productUri: seoUri,
      limit: 1
    };
    return this._httpClient.post<Product[]>(
      `${this._environmentService.getApiUrl()}/${environment.qart.apiVersion}/merchants/${this._merchant._id}/products/search`,
      filters,
      {
        headers: new HttpHeaders().set('Content-Type', 'application/json'),
        withCredentials: true
      })
    .pipe(
      catchError((error: HttpErrorResponse) => {
        console.log('Error:', error);
        return of([]);
      }),
      map((products: Product[]) => products.length > 0 ? this.restoreProduct(products[0]) : null),
      // Check if the product belongs to a hidden category
        map((product: Product) => {
          if (product && this._categories && this._categories.length > 0) {
            const category: Category = this._categories.find(category => category._id === product.categoryId);
            if (category && !category.displayOnline) {
              return null;
            }
          }
          return product;
        }),
      // Handle the logic of the cache and the propagation
      tap((product: Product) => {
        if (options.cache) {
          this._cacheService.set(this._cacheNamespace, 'product', cacheParams, product, options.expire);
        }
        if (options.propagate) {
          this._product.next(product);
        }
      })
    );
  }

  /**
   * Retrieves the product attributes based on the given filters.
   * @param filters - The filters to apply to the product attributes.
   * @param options - The options related to cache and propagation.
   * @returns The product attributes.
   */
  getProductAttributes(filters: any = {}, options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Observable<any> {
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return of(null);
    }
    // If a specific category is not specified, then make sure to only retrieve attributes of products from categories that are displayed online
    if (!('categoryIds' in filters) && this._categories && this._categories.length > 0) {
      filters['categoryIds'] = this._categories
        .filter(category => category.displayOnline)
        .map(category => category._id);
    }
    // If the attributes are already loaded, return them
    const cacheParams: any = { merchantId: this._merchant._id, filters };
    if (options.cache && this._cacheService.has(this._cacheNamespace, 'product-attributes', cacheParams)) {
      const attribute: any = this._cacheService.get(this._cacheNamespace, 'product-attributes', cacheParams);
      return of(attribute);
    }
    // Otherwise, load the attributes from the backend
    return this._httpClient.post<any>(
      `${this._environmentService.getApiUrl()}/${environment.qart.apiVersion}/merchants/${this._merchant._id}/products/attributes`,
      filters,
      {
        headers: new HttpHeaders().set('Content-Type', 'application/json'),
        withCredentials: true
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(`Error: ${error}`);
          return of(null);
        }),
        // Handle the logic of the cache and the propagation
        tap((attributes: any) => {
          if (!attributes) {
            return;
          }
          if (options.cache) {
            this._cacheService.set(this._cacheNamespace, 'product-attributes', cacheParams, attributes, options.expire);
          }
        })
      );
  }

  /**
   * Retrieves product tags based on the given filters.
   * @param filters - The filters to apply to the search.
   * @param options - The options related to cache and propagation.
   * @returns The product tags.
   */
  getProductTags(filters: any = {}, options: HttpOptions=DEFAULT_HTTP_OPTIONS_NO_EXPIRE): Observable<string[]> {
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return of([]);
    }
    // If a specific category is not specified, then make sure to only retrieve tags of products from categories that are displayed online
    if (!('categoryIds' in filters) && this._categories && this._categories.length > 0) {
      filters['categoryIds'] = this._categories
        .filter(category => category.displayOnline)
        .map(category => category._id);
    }
    // If the tags are already loaded, return them
    const cacheParams: any = { merchantId: this._merchant._id, filters };
    if (options.cache && this._cacheService.has(this._cacheNamespace, 'tags', cacheParams)) {
      const tags: string[] = this._cacheService.get(this._cacheNamespace, 'tags', cacheParams);
      return of(tags);
    }
    // Otherwise, load the tags from the backend
    return this._httpClient.post<string[]>(
      `${this._environmentService.getApiUrl()}/${environment.qart.apiVersion}/merchants/${this._merchant._id}/products/tags/search`,
      filters,
      {
        headers: new HttpHeaders().set('Content-Type', 'application/json'),
        withCredentials: true
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error(`Error: ${error}`);
          return of([]);
        }),
        map((tags: string[]) => tags.filter(tag => tag != null).sort((a, b) => a.localeCompare(b))),
        tap((tags: string[]) => {
          if (options.cache) {
            this._cacheService.set(this._cacheNamespace, 'tags', cacheParams, tags, options.expire);
          }
        })
      );
  }

  /**
   * Get the URLs of the photos of a product or a variant item.
   * @param {Product | VariantItem} obj - The product or variant item.
   * @returns {string[]} The URLs of the photos.
   */
  getPhotoUrls(obj: Product | VariantItem): string[] {
    if (!this._merchant) {
      console.log('Error: No merchant.');
      return [];
    }
    let urls: string[] = [];
    if (obj.photos && obj.photos.length > 0) {
      urls = obj.photos.map((photo) => {
        if (photo.source !== 'qart') {
          return photo.url;
        } else {
          const userPreferences: string = photo.useImage;
          const webpAvailable: boolean = photo.webpFilename && photo.webpFilename !== '';
          const aiModifiedAvailable: boolean = photo.aiModifiedFilename && photo.aiModifiedFilename !== '';
          if (userPreferences === 'original' || userPreferences === 'webp') {
            if (webpAvailable) {
              return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.webpFilename}`;
            } else {
              return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.filename}`;
            }
          } else if (userPreferences === 'aiModified') {
            if (aiModifiedAvailable) {
              return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.aiModifiedFilename}`;
            } else if (webpAvailable) {
              return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.webpFilename}`;
            } else {
              return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.filename}`;
            }
          } else {
            return `${environment.qart.imagesUrl}/${this._merchant.name}/${photo.filename}`;
          }
        }
      });
    }
    return urls;
  }

  /**
   * Get the SrcSet URLs and sizes of the photos of the merchant.
   * @param {Product | VariantItem} obj - The product or variant item.
   * @param {number} photoIdx - The index of the photo.
   * @returns {{srcset: string, sizes: string}} The SrcSet URLs and sizes of the photos.
   */
  getPhotoSrcSet(obj: Product | VariantItem, photoIdx: number): {srcset: string, sizes: string} {
    const response: {srcset: string, sizes: string} = {
      srcset: null,
      sizes: null
    };
    if (!this._merchant) {
      return response;
    }
    if (obj.photos && obj.photos.length < photoIdx) {
      return response;
    }
    const photo: Picture = obj.photos[photoIdx];
    if (photo.source !== 'qart') {
      return response;
    }
    const userPreferences: string = photo.useImage;
    const srcSetWebpAvailable: boolean = photo.webpSrcSetFilenames && photo.webpSrcSetFilenames?.length > 0;
    const srcSetAiModifiedAvailable: boolean = photo.aiModifiedWebpSrcSetFilenames && photo.aiModifiedWebpSrcSetFilenames?.length > 0;
    if ((userPreferences === 'original' || userPreferences === 'webp') && srcSetWebpAvailable) {
      return this._getSrcSet(this._merchant.name, photo.webpSrcSetFilenames);
    }
    else if (userPreferences === 'aiModified') {
      if (srcSetAiModifiedAvailable) {
        return this._getSrcSet(this._merchant.name, photo.aiModifiedWebpSrcSetFilenames);
      } else if (srcSetWebpAvailable) {
        return this._getSrcSet(this._merchant.name, photo.webpSrcSetFilenames);
      }
    }
    return response;
  }
}
